/**
 * PreJoinPage — candidate-facing Live Interview pre-join flow.
 * Unauthenticated: the URL's `token` is the credential (see
 * services/liveInterviewPublicApi.ts). Three screens, in the same order and
 * visual language as the Proctored Assessment candidate flow
 * (pages/publicInterview/PublicInterviewPage.tsx): consent -> device checks
 * -> ID verification -> join the room. That page exports none of its shell/
 * row components, so this file keeps its own local copies restyled to match
 * rather than importing across products.
 *
 * Checks/behavior kept from the pre-restyle version: camera/mic,
 * single/duplicate-display, best-effort geolocation, and an "integrity
 * monitor" that only ever logs tab-hidden events — this app has no way to
 * inspect other running apps/processes, so that's the honest limit of what
 * it claims to watch. Deliberately NOT added: a mandatory screen-share/
 * recording requirement. Unlike the Proctored Assessment (no live recruiter,
 * so the candidate's own browser must record itself), Live Interview already
 * has a live recruiter present and recording is composited recruiter-side
 * (useLiveInterviewRecorder.ts) — requiring the candidate to also share
 * their entire screen here would be a new, separate feature, not a design
 * match.
 *
 * One deliberate deviation from the pre-restyle version, carried forward:
 * its getUserMedia() success handler assigns the resolved stream into
 * component state unconditionally, with no check for whether the component
 * had already been destroyed while the permission prompt was pending — a
 * real leak (the camera/mic stays live with nothing left to call stop() on
 * it once that happens). Guarded here via `unmountedRef`/`mediaStreamRef`,
 * checked inside the getUserMedia() promise's resolution before touching
 * state or keeping the stream alive.
 */

import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import {
  CheckCircleIcon, XCircleIcon, ShieldCheckIcon, VideoCameraIcon,
  ComputerDesktopIcon, MapPinIcon, UserCircleIcon, BriefcaseIcon,
} from '@heroicons/react/24/outline'

import LoadingSpinner from '@/components/common/LoadingSpinner'
import LegalDocumentModal from '@/components/common/LegalDocumentModal'
import { TERMS_OF_SERVICE_MD } from '@/content/legal/termsOfService'
import { PRIVACY_POLICY_MD } from '@/content/legal/privacyPolicy'
import useIdCaptureGuide from '@/hooks/useIdCaptureGuide'
import {
  getSession, recordIntegrityEvent, confirmConsent, submitIdVerification,
  type LiveInterviewPublicSession,
} from '@/services/liveInterviewPublicApi'

type Stage = 'loading' | 'verify' | 'precheck' | 'id_verification' | 'error'
type CheckStatus = 'pending' | 'checking' | 'done' | 'failed'

interface CameraMicCheck { status: CheckStatus }
// Always flips to 'done' once run, whatever the outcome — informational only.
interface DuplicateScreenCheck { status: CheckStatus; multiScreenDetected: boolean }
// Always flips to 'done' once run, whatever the outcome — informational only.
interface GeolocationCheck { status: CheckStatus; denied: boolean }
interface IntegrityMonitorCheck { status: CheckStatus }

export default function PreJoinPage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()

  const [stage, setStage] = useState<Stage>('loading')
  const [screen, setScreen] = useState<LiveInterviewPublicSession | null>(null)
  const [sessionError, setSessionError] = useState('')

  // ---- Consent screen ----
  const [policyAgreed, setPolicyAgreed] = useState(false)
  const [openLegalDoc, setOpenLegalDoc] = useState<'terms' | 'privacy' | null>(null)
  const [confirmingConsent, setConfirmingConsent] = useState(false)

  // ---- Device checks (precheck) ----
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null)
  const [cameraMic, setCameraMic] = useState<CameraMicCheck>({ status: 'pending' })
  const [duplicateScreen, setDuplicateScreen] = useState<DuplicateScreenCheck>({ status: 'pending', multiScreenDetected: false })
  const [geolocation, setGeolocation] = useState<GeolocationCheck>({ status: 'pending', denied: false })
  const [integrityMonitor, setIntegrityMonitor] = useState<IntegrityMonitorCheck>({ status: 'pending' })
  // Guards the precheck-start effect below against re-firing if `stage`
  // ever re-renders while still 'precheck' (it only ever runs once).
  const precheckStartedRef = useRef(false)
  const precheckVideoRef = useRef<HTMLVideoElement>(null)

  // Flipped true on unmount, checked inside getUserMedia()/
  // getCurrentPosition callbacks before touching state — see file header re:
  // the destroy-race camera leak this avoids.
  const unmountedRef = useRef(false)
  // Mirrors `mediaStream` outside React state so the unmount cleanup effect
  // (which only ever runs once, closed over whatever was true at mount) can
  // still reach — and stop — a stream that arrives after unmount was flagged.
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const integrityArmedRef = useRef(false)

  // ---- ID verification ----
  const [idPhotoSnapshot, setIdPhotoSnapshot] = useState<string | null>(null)
  const [submittingId, setSubmittingId] = useState(false)
  const idVerificationVideoRef = useRef<HTMLVideoElement>(null)
  // Mirrors idVerificationVideoRef.current into state — a plain ref's
  // .current isn't available until after the render that mounts it, so
  // useIdCaptureGuide (which needs the element as a prop to react to it) is
  // handed this instead of reading the ref directly during render.
  const [idVideoEl, setIdVideoEl] = useState<HTMLVideoElement | null>(null)
  const idCaptureGuide = useIdCaptureGuide(idVideoEl, (stage === 'precheck' || stage === 'id_verification') && !idPhotoSnapshot)

  const logEvent = (eventType: string, eventMetadata?: Record<string, any>) => {
    if (!token) return
    recordIntegrityEvent(token, eventType, eventMetadata).catch(() => {})
  }

  const runCameraMicCheck = () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraMic({ status: 'failed' })
      logEvent('permission_denied')
      return
    }
    setCameraMic({ status: 'checking' })
    // Echo cancellation requested explicitly, matching the room pages this
    // check is a dress rehearsal for — a bare `audio: true` leaves it to the
    // browser's implicit default.
    navigator.mediaDevices.getUserMedia({
      video: true,
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    })
      .then((stream) => {
        if (unmountedRef.current) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        mediaStreamRef.current?.getTracks().forEach((t) => t.stop())
        mediaStreamRef.current = stream
        setMediaStream(stream)
        setCameraMic({ status: 'done' })
        logEvent('camera_mic_granted')
      })
      .catch(() => {
        if (unmountedRef.current) return
        setCameraMic({ status: 'failed' })
        logEvent('permission_denied')
      })
  }

  // Also re-run by the row's "Check again" button (after the candidate disconnects the extra
  // display). Logs the integrity event only when an extra display is newly found, so repeated
  // re-checks don't record it over and over.
  const runDuplicateScreenCheck = () => {
    const isExtended = (window.screen as any)?.isExtended === true
    const alreadyFlagged = duplicateScreen.status === 'done' && duplicateScreen.multiScreenDetected
    setDuplicateScreen({ status: 'done', multiScreenDetected: isExtended })
    if (isExtended && !alreadyFlagged) logEvent('multi_screen_detected')
  }

  const runGeolocationCheck = () => {
    if (!navigator.geolocation) {
      setGeolocation({ status: 'done', denied: true })
      logEvent('geolocation_denied')
      return
    }
    setGeolocation((g) => ({ ...g, status: 'checking' }))
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (unmountedRef.current) return
        setGeolocation({ status: 'done', denied: false })
        logEvent('geolocation_captured', { lat: pos.coords.latitude, lng: pos.coords.longitude })
      },
      () => {
        if (unmountedRef.current) return
        setGeolocation({ status: 'done', denied: true })
        logEvent('geolocation_denied')
      },
      { timeout: 15000, maximumAge: 60000 }
    )
  }

  // Real signal from here on: fires on tab-switch, minimize, or Alt-Tab to
  // another app. Armed once (integrityArmedRef guards re-arming on a
  // duplicate call) and only ever removed on unmount below.
  const onVisibilityChange = () => {
    if (document.hidden) logEvent('tab_hidden')
  }

  const armIntegrityMonitor = () => {
    if (integrityArmedRef.current) return
    integrityArmedRef.current = true
    document.addEventListener('visibilitychange', onVisibilityChange)
    setIntegrityMonitor({ status: 'done' })
    logEvent('integrity_monitor_armed')
  }

  useEffect(() => {
    if (!token) {
      setSessionError('This meeting link is invalid or has expired.')
      setStage('error')
      return
    }
    getSession(token)
      .then((data) => {
        if (unmountedRef.current) return
        setScreen(data)
        setStage('verify')
      })
      .catch(() => {
        if (unmountedRef.current) return
        setSessionError('This meeting link is invalid or has expired.')
        setStage('error')
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  // Camera/mic + display/geolocation checks and the integrity-monitor arm
  // only start once the candidate reaches the device-check screen — i.e.
  // after they've actually consented on the screen before it, same as
  // PublicInterviewPage never touching the camera before its own consent
  // screen.
  useEffect(() => {
    if (stage !== 'precheck' || precheckStartedRef.current) return
    precheckStartedRef.current = true
    runCameraMicCheck()
    runDuplicateScreenCheck()
    runGeolocationCheck()
    armIntegrityMonitor()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage])

  // Attaches the live stream to the precheck row's thumbnail preview via a
  // stable ref + effect, not an inline `ref={(el) => ...}` callback — an
  // inline arrow function gets a new identity every render, which makes
  // React detach/reattach the ref (and re-null srcObject) constantly,
  // causing an on/off flicker. Same fix as the ID Verification video below.
  useEffect(() => {
    if (stage !== 'precheck') return
    if (precheckVideoRef.current) precheckVideoRef.current.srcObject = mediaStream
  }, [stage, mediaStream])

  // Binds the same stream to the ID Verification screen's <video>, reusing
  // it rather than re-requesting getUserMedia (which would re-prompt for
  // permission and flicker the preview).
  useEffect(() => {
    if (stage !== 'id_verification' || idPhotoSnapshot) { setIdVideoEl(null); return }
    const el = idVerificationVideoRef.current
    if (el) el.srcObject = mediaStream
    setIdVideoEl(el)
  }, [stage, mediaStream, idPhotoSnapshot])

  // Release the camera/mic and detach the tab-visibility listener on
  // unmount — even if the candidate never reaches Join Meeting. Runs once;
  // reaches through mediaStreamRef/unmountedRef rather than the
  // `mediaStream` state closure so a stream that resolves after this fires
  // is still caught by the unmountedRef check inside runCameraMicCheck's
  // .then() above.
  useEffect(() => {
    return () => {
      unmountedRef.current = true
      document.removeEventListener('visibilitychange', onVisibilityChange)
      mediaStreamRef.current?.getTracks().forEach((t) => t.stop())
      mediaStreamRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // One still frame from a live <video> element — best-effort, for the
  // hiring team's record only.
  const captureSnapshotFromVideo = (video: HTMLVideoElement | null): string | null => {
    if (!video || !video.videoWidth) return null
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    return canvas.toDataURL('image/png')
  }

  const handleConfirmAndContinue = async () => {
    if (!token) return
    setConfirmingConsent(true)
    try {
      await confirmConsent(token)
      setStage('precheck')
    } catch {
      toast.error('Something went wrong. Please try again.')
    } finally {
      setConfirmingConsent(false)
    }
  }

  const allChecksDone =
    cameraMic.status === 'done' &&
    duplicateScreen.status === 'done' &&
    geolocation.status === 'done' &&
    integrityMonitor.status === 'done'

  const handleJoinMeeting = async () => {
    if (!token) return
    setSubmittingId(true)
    try {
      if (idPhotoSnapshot) await submitIdVerification(token, idPhotoSnapshot)
    } catch {
      // Best-effort, same as every other integrity/recording upload in this
      // flow — a failed upload must not strand the candidate outside their
      // own live interview.
      toast.error('Could not save your ID verification photo — continuing anyway.')
    } finally {
      setSubmittingId(false)
    }
    navigate(`/live-interview/room/${token}`)
  }

  if (stage === 'loading') {
    return (
      <PageShell>
        <CenteredShell><LoadingSpinner size="lg" /></CenteredShell>
      </PageShell>
    )
  }

  if (stage === 'error') {
    return (
      <PageShell>
        <CenteredShell>
          <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <ShieldCheckIcon className="w-7 h-7 text-red-400" />
          </div>
          <p className="text-gray-700">{sessionError}</p>
        </CenteredShell>
      </PageShell>
    )
  }

  if (stage === 'verify') {
    return (
      <PageShell>
        <CenteredShell xl>
          <ScreenHeader
            icon={ShieldCheckIcon}
            title="Talent OS Live Interview"
            subtitle="Please confirm your details below before you begin."
          />

          {(screen?.candidate_name || screen?.job_title) && (
            <div className="bg-gray-50 rounded-xl border border-gray-100 divide-y divide-gray-100 mb-6">
              {screen?.candidate_name && <DetailRow icon={UserCircleIcon} label="Candidate" value={screen.candidate_name} />}
              {screen?.job_title && <DetailRow icon={BriefcaseIcon} label="Position" value={screen.job_title} />}
            </div>
          )}

          <div className="mb-5">
            <p className="text-sm font-semibold text-gray-900 mb-1.5">Consent to AI-Assisted Monitoring</p>
            <p className="text-xs text-gray-500 leading-relaxed mb-2">
              This live interview uses TalentOS's AI-assisted monitoring to help ensure a fair and secure
              interview experience. By proceeding, you acknowledge and agree that:
            </p>
            <ul className="text-xs text-gray-500 leading-relaxed mb-2 list-disc pl-4 space-y-1">
              <li>Your camera and microphone will be used throughout the interview, and the session will be recorded.</li>
              <li>
                TalentOS will perform automated identity verification (including a photo captured before the
                interview begins) and monitor tab/window activity during the session.
              </li>
              <li>
                This data, along with your responses, will be analyzed by TalentOS's systems and shared only
                with the hiring team for the role you applied to.
              </li>
              <li>
                Your data will be handled as described in TalentOS's{' '}
                <LegalLink onOpen={() => setOpenLegalDoc('terms')}>Terms and Conditions</LegalLink> and{' '}
                <LegalLink onOpen={() => setOpenLegalDoc('privacy')}>Privacy Policy</LegalLink>, which you should
                read before continuing.
              </li>
            </ul>
            <p className="text-xs text-gray-500 leading-relaxed mb-3">
              If you do not consent, you will not be able to proceed with this interview.
            </p>
            <ChecklistItem
              checked={policyAgreed}
              onChange={setPolicyAgreed}
              label={
                <>
                  I have read and agree to the{' '}
                  <LegalLink onOpen={() => setOpenLegalDoc('terms')}>Terms and Conditions</LegalLink> and{' '}
                  <LegalLink onOpen={() => setOpenLegalDoc('privacy')}>Privacy Policy</LegalLink>, and I consent to
                  the recording and AI-assisted monitoring described above.
                </>
              }
            />
          </div>
          <button
            onClick={handleConfirmAndContinue}
            disabled={confirmingConsent || !policyAgreed}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 transition-colors shadow-sm shadow-blue-200"
          >
            {confirmingConsent ? <LoadingSpinner size="sm" /> : null}
            {confirmingConsent ? 'Continuing…' : 'Continue'}
          </button>

          <LegalDocumentModal
            open={openLegalDoc !== null}
            title={openLegalDoc === 'privacy' ? 'Privacy Policy' : 'Terms and Conditions'}
            content={openLegalDoc === 'privacy' ? PRIVACY_POLICY_MD : TERMS_OF_SERVICE_MD}
            onClose={() => setOpenLegalDoc(null)}
          />
        </CenteredShell>
      </PageShell>
    )
  }

  if (stage === 'precheck') {
    return (
      <PageShell>
        <CenteredShell xl>
          <ScreenHeader
            icon={ShieldCheckIcon}
            title="Before You Begin"
            subtitle="Please complete the checks below before joining."
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 items-start">
            <CheckRow
              icon={VideoCameraIcon}
              title="Camera & microphone access"
              status={cameraMic.status}
              pendingLabel="Waiting for permission…"
              doneLabel="Verified"
              failedLabel="Access denied — please allow camera and microphone access."
              onRetry={cameraMic.status === 'failed' ? runCameraMicCheck : undefined}
              preview={mediaStream ? (
                <video
                  autoPlay muted playsInline
                  ref={precheckVideoRef}
                  className="w-16 h-12 rounded-md object-cover bg-black flex-shrink-0"
                />
              ) : undefined}
            />

            <CheckRow
              icon={ComputerDesktopIcon}
              title="Single display check"
              status={duplicateScreen.status}
              pendingLabel="Checking connected displays…"
              doneLabel={duplicateScreen.multiScreenDetected
                ? 'Extended display detected — flagged, you may still continue.'
                : 'Verified — only one display detected'}
              doneTone={duplicateScreen.multiScreenDetected ? 'warn' : 'ok'}
              // Same as the Proctored precheck: only offered once an extra display was found -
              // the one state where the candidate has something to do (disconnect it).
              onRetry={duplicateScreen.status === 'done' && duplicateScreen.multiScreenDetected ? runDuplicateScreenCheck : undefined}
              retryLabel="Check again"
            />

            <CheckRow
              icon={MapPinIcon}
              title="Location"
              status={geolocation.status}
              pendingLabel="Waiting for permission…"
              doneLabel={geolocation.denied
                ? 'Location unavailable — flagged, you may still continue.'
                : 'Location captured'}
              doneTone={geolocation.denied ? 'warn' : 'ok'}
              onRetry={geolocation.status === 'done' && geolocation.denied ? runGeolocationCheck : undefined}
            />
          </div>

          <button
            onClick={() => setStage('id_verification')}
            disabled={!allChecksDone}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 transition-colors shadow-sm shadow-blue-200"
          >
            Continue
          </button>
        </CenteredShell>
      </PageShell>
    )
  }

  // stage === 'id_verification'
  return (
    <PageShell>
      <CenteredShell xl>
        <ScreenHeader
          icon={ShieldCheckIcon}
          title="ID Verification"
          subtitle="Hold your ID card up next to your face so we can capture a photo for the hiring team's records."
        />

        <div className="grid grid-cols-1 sm:grid-cols-5 gap-6 mb-6">
          <div className="sm:col-span-2">
            <p className="text-sm font-semibold text-gray-900 mb-2">Instructions</p>
            <ul className="text-xs text-gray-500 leading-relaxed list-disc pl-4 space-y-1.5">
              <li>Have your ID card ready</li>
              <li>Make sure your face and your ID are both clearly visible</li>
              <li>Hold your ID next to your face, not covering it</li>
              <li>Click Capture when you're ready</li>
            </ul>
            <p className="text-xs text-gray-400 mt-4">
              This photo is saved with your interview for the hiring team's identity check — it is not analyzed
              automatically, and no one is watching live.
            </p>
          </div>
          <div className="sm:col-span-3">
            {idPhotoSnapshot ? (
              <img
                src={idPhotoSnapshot}
                alt="Captured ID verification photo"
                className="w-full aspect-video object-cover rounded-xl border border-gray-200 bg-black"
              />
            ) : (
              <>
                <video
                  autoPlay muted playsInline
                  ref={idVerificationVideoRef}
                  className="w-full aspect-video object-cover rounded-xl border border-gray-200 bg-black"
                />
                <p className={`text-xs font-medium mt-2 flex items-center gap-1.5 ${
                  idCaptureGuide.status === 'ready' ? 'text-green-600'
                    : idCaptureGuide.status === 'loading' || idCaptureGuide.status === 'unavailable' ? 'text-gray-400'
                    : 'text-amber-600'
                }`}>
                  {idCaptureGuide.status === 'ready' && <CheckCircleIcon className="w-3.5 h-3.5" />}
                  {idCaptureGuide.message}
                </p>
              </>
            )}
          </div>
        </div>

        {idPhotoSnapshot && (
          <div className="mb-5 flex items-center gap-1.5 text-sm text-green-600">
            <CheckCircleIcon className="w-5 h-5" /> ID photo captured
          </div>
        )}

        <div className="flex gap-3">
          {idPhotoSnapshot && (
            <button
              onClick={() => setIdPhotoSnapshot(null)}
              disabled={submittingId}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-600 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors disabled:opacity-40"
            >
              Retake
            </button>
          )}
          <button
            onClick={() => {
              if (!idPhotoSnapshot) {
                // Re-checked at the moment of click, not just shown live above —
                // a candidate can click faster than the ~500ms detection tick.
                // 'unavailable' (models failed to load) falls back to trusting
                // manual capture rather than blocking it forever.
                if (idCaptureGuide.status !== 'ready' && idCaptureGuide.status !== 'unavailable') {
                  toast.error(idCaptureGuide.message)
                  return
                }
                const snap = captureSnapshotFromVideo(idVerificationVideoRef.current)
                if (snap) setIdPhotoSnapshot(snap)
                else toast.error('Could not capture a photo — please try again.')
              } else {
                handleJoinMeeting()
              }
            }}
            disabled={submittingId}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 transition-colors shadow-sm shadow-blue-200"
          >
            {submittingId ? <LoadingSpinner size="sm" /> : null}
            {idPhotoSnapshot ? (submittingId ? 'Joining…' : 'Join Meeting') : 'Capture'}
          </button>
        </div>
      </CenteredShell>
    </PageShell>
  )
}

// ============================================================================
// Shell / row components — local copies of PublicInterviewPage's visual
// language (that file exports none of them), restyled for this flow.
// ============================================================================

function PageShell({ children }: { children: React.ReactNode }) {
  return <div className="min-h-screen bg-gray-50">{children}</div>
}

function CenteredShell({ children, xl }: { children: React.ReactNode; xl?: boolean }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className={`bg-white rounded-2xl shadow-xl shadow-gray-200/60 border border-gray-100 p-8 ${
        xl ? 'max-w-4xl w-full text-left' : 'max-w-sm w-full text-center'
      }`}>
        {children}
      </div>
    </div>
  )
}

function ScreenHeader({ icon: Icon, title, subtitle }: {
  icon: React.FC<{ className?: string }>
  title: string
  subtitle?: string
}) {
  return (
    <div className="flex items-start gap-3.5 mb-6">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-blue-50 text-blue-600">
        <Icon className="w-6 h-6" />
      </div>
      <div className="min-w-0 pt-0.5">
        <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  )
}

function DetailRow({ icon: Icon, label, value }: { icon: React.FC<{ className?: string }>; label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <span className="flex items-center gap-2 text-sm text-gray-500">
        <Icon className="w-4 h-4 text-gray-400" /> {label}
      </span>
      <span className="text-sm text-gray-900 font-medium">{value}</span>
    </div>
  )
}

// Inline link that opens a legal document over the consent screen. Rendered
// as a <button>, not an <a>, because one instance sits inside the consent
// checkbox's <label> — without preventDefault/stopPropagation, a candidate
// opening the Privacy Policy would silently tick their own consent box.
function LegalLink({ onOpen, children }: { onOpen: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onOpen()
      }}
      className="text-blue-600 hover:text-blue-700 underline font-medium"
    >
      {children}
    </button>
  )
}

// `label` is a ReactNode rather than a string so the consent copy can embed
// the Terms/Privacy links above.
function ChecklistItem({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: React.ReactNode }) {
  return (
    <label className="flex items-start gap-2.5 cursor-pointer group">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
      />
      <span className="text-sm text-gray-600 group-hover:text-gray-800">{label}</span>
    </label>
  )
}

function CheckRow({
  icon: Icon, title, status, pendingLabel, doneLabel, failedLabel, doneTone = 'ok', onRetry, retryLabel = 'Try again', preview,
}: {
  icon: React.FC<{ className?: string }>
  title: string
  status: CheckStatus
  pendingLabel: string
  doneLabel: string
  failedLabel?: string
  doneTone?: 'ok' | 'warn'
  onRetry?: () => void
  retryLabel?: string
  preview?: React.ReactNode
}) {
  const done = status === 'done'
  const failed = status === 'failed'
  return (
    <div className="flex items-start gap-3 p-3.5 rounded-xl border border-gray-100 bg-gray-50">
      <div className="w-9 h-9 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
        {failed ? <XCircleIcon className="w-4 h-4 text-red-500" /> : <Icon className="w-4 h-4 text-gray-500" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900">{title}</p>
        {(status === 'pending' || status === 'checking') && (
          <p className="text-xs text-gray-400 mt-0.5">{pendingLabel}</p>
        )}
        {done && (
          <p className={`text-xs mt-0.5 flex items-center gap-1 ${doneTone === 'warn' ? 'text-amber-600' : 'text-green-600'}`}>
            {doneTone === 'ok' && <CheckCircleIcon className="w-3.5 h-3.5" />}
            {doneLabel}
          </p>
        )}
        {failed && <p className="text-xs text-red-500 mt-0.5">{failedLabel}</p>}
      </div>
      {preview}
      {onRetry && (
        <button
          onClick={onRetry}
          className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 flex-shrink-0"
        >
          {retryLabel}
        </button>
      )}
    </div>
  )
}
